import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Check, Loader2, FileText, Moon, Stethoscope, Thermometer, Apple, AlertCircle, Activity, Trophy, BarChart3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = { FileText, Moon, Stethoscope, Thermometer, Apple, Activity, BarChart3 };

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const TimePicker = ({ value, onChange, isUnanswered, testId }) => {
  const parsed = value ? value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i) : null;
  const [hour, setHour] = useState(parsed ? parsed[1] : '');
  const [minute, setMinute] = useState(parsed ? parsed[2] : '');
  const [period, setPeriod] = useState(parsed ? parsed[3] : 'PM');

  const update = (h, m, p) => {
    if (h && m) onChange(`${h}:${m} ${p}`);
  };

  const selectClass = (filled) => `flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold text-center appearance-none bg-white focus:border-[#467322] focus:ring-1 focus:ring-[#467322]/20 ${
    isUnanswered && !filled ? 'border-red-300' : 'border-gray-200'
  }`;

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <select value={hour} onChange={e => { setHour(e.target.value); update(e.target.value, minute, period); }}
        className={selectClass(hour)} data-testid={`${testId}-hour`}>
        <option value="">Hora</option>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-xl font-black text-gray-300">:</span>
      <select value={minute} onChange={e => { setMinute(e.target.value); update(hour, e.target.value, period); }}
        className={selectClass(minute)} data-testid={`${testId}-min`}>
        <option value="">Min</option>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <div className="flex rounded-xl border border-gray-200 overflow-hidden">
        {['AM', 'PM'].map(p => (
          <button key={p} onClick={() => { setPeriod(p); update(hour, minute, p); }}
            className={`px-3 py-2.5 text-xs font-bold transition-all ${
              period === p ? 'bg-[#467322] text-white' : 'bg-white text-gray-400 hover:bg-gray-50'
            }`} data-testid={`${testId}-${p.toLowerCase()}`}>
            {p}
          </button>
        ))}
      </div>
    </div>
  );
};

const NumberInput = ({ value, onChange, unit, min, max, isUnanswered, testId }) => {
  const numVal = value === '' ? '' : Number(value);
  const decrement = () => {
    const next = Math.max(min, (numVal || 0) - 1);
    onChange(String(next));
  };
  const increment = () => {
    const next = Math.min(max, (numVal || 0) + 1);
    onChange(String(next));
  };

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <button onClick={decrement}
        className="w-11 h-11 rounded-xl border border-gray-200 flex items-center justify-center text-lg font-bold text-gray-400 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        data-testid={`${testId}-minus`}>
        -
      </button>
      <div className="flex-1 relative">
        <input
          type="number"
          value={value}
          onChange={e => {
            const v = e.target.value;
            if (v === '') { onChange(''); return; }
            const n = Number(v);
            if (!isNaN(n) && n >= min && n <= max) onChange(String(n));
          }}
          min={min} max={max}
          placeholder="0"
          className={`w-full text-center rounded-xl border px-4 py-2.5 text-lg font-bold text-[#1A2E10] focus:border-[#467322] focus:ring-1 focus:ring-[#467322]/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            isUnanswered ? 'border-red-300' : 'border-gray-200'
          }`}
          data-testid={`${testId}-input`}
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">{unit}</span>
        )}
      </div>
      <button onClick={increment}
        className="w-11 h-11 rounded-xl border border-gray-200 flex items-center justify-center text-lg font-bold text-[#467322] hover:bg-green-50 active:bg-green-100 transition-colors"
        data-testid={`${testId}-plus`}>
        +
      </button>
    </div>
  );
};

const BRISTOL_DATA_LIST = [
  { id: 1, color: '#92400E', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/af9a06f2f2e2e8e71a737f282f34d2e0da362e8207b417305e8eb9ac61100e71.png' },
  { id: 2, color: '#B45309', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/3bb9939d232ea8fdc1b1029ed02ee8b2f3bc8bf74e05bdeb48de1af2f662dae7.png' },
  { id: 3, color: '#D97706', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/67f6822683be4524f4064bdb641e2907c58328f1bd8af8db565a80f2998922ee.png' },
  { id: 4, color: '#059669', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/69679dc5adee0cb580b65fd625fb7b21c418eb1e9a273c7cde13dca7bd093a63.png' },
  { id: 5, color: '#0D9488', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/da3f145043633ad7e97c53468a577e9745087957d26d4d179163d9b6227500de.png' },
  { id: 6, color: '#DC2626', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/e4a494fc096a953c77eb7abfc90863dc24f302003936b2febb17ad7d4dd3a544.png' },
  { id: 7, color: '#9333EA', image: 'https://static.prod-images.emergentagent.com/jobs/11ecd890-ad75-4a9f-a1f9-7cbcff8ce01e/images/748402d2cdf2ddbb4e6855e44d6d9b2c460b271a3fcd4dd5cc2a035625faa15a.png' },
];

/* ── Scoring helpers ──────────────────────────────────────── */
const SEVERITY_COLORS = {
  'N/A': '#9CA3AF',
  'Leve': '#F59E0B',
  'Moderado': '#F97316',
  'Grave': '#EF4444',
  'Sí': '#D97706',
  'No': '#059669',
};

const calculateScores = (template, answers) => {
  const blocks = template.blocks || [];
  const blockScores = blocks.map(block => {
    let total = 0;
    let maxPossible = 0;
    (block.questions || []).forEach(q => {
      if (q.type !== 'scored_select') return;
      const selectedValue = answers[q.id];
      const opts = q.options || [];
      const maxOpt = Math.max(...opts.map(o => o.points || 0));
      maxPossible += maxOpt;
      if (selectedValue) {
        const match = opts.find(o => o.value === selectedValue);
        if (match) total += (match.points || 0);
      }
    });
    return { blockId: block.id, name: block.name, score: total, maxPossible, color: block.color };
  });

  const grandTotal = blockScores.reduce((s, b) => s + b.score, 0);
  const maxTotal = blockScores.reduce((s, b) => s + b.maxPossible, 0);
  return { blocks: blockScores, grandTotal, maxTotal };
};

/* ── Scoring Results Screen ───────────────────────────────── */
const ScoringResults = ({ template, scores, onClose }) => {
  const pct = scores.maxTotal > 0 ? (scores.grandTotal / scores.maxTotal) * 100 : 0;
  const interp = template.scoring_interpretation;
  const isPerGroup = interp?.type === 'per_group';

  const getLevelFromRanges = (total, ranges) => {
    const bgMap = { '#059669': 'bg-emerald-50', '#F59E0B': 'bg-amber-50', '#F97316': 'bg-orange-50', '#EF4444': 'bg-red-50' };
    const borderMap = { '#059669': 'border-emerald-200', '#F59E0B': 'border-amber-200', '#F97316': 'border-orange-200', '#EF4444': 'border-red-200' };
    for (const r of ranges) {
      if (total >= r.min && total <= r.max) {
        return { label: r.label, color: r.color, bg: bgMap[r.color] || 'bg-gray-50', border: borderMap[r.color] || 'border-gray-200', description: r.description, advice: r.advice, warning: r.warning };
      }
    }
    const last = ranges[ranges.length - 1];
    return { label: last.label, color: last.color, bg: bgMap[last.color] || 'bg-red-50', border: borderMap[last.color] || 'border-red-200', description: last.description, advice: last.advice, warning: last.warning };
  };

  const getDefaultLevel = (percent) => {
    if (percent <= 25) return { label: 'Bajo', color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200' };
    if (percent <= 50) return { label: 'Moderado', color: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-200' };
    if (percent <= 75) return { label: 'Alto', color: '#F97316', bg: 'bg-orange-50', border: 'border-orange-200' };
    return { label: 'Muy Alto', color: '#EF4444', bg: 'bg-red-50', border: 'border-red-200' };
  };

  const customRanges = template.scoring_ranges;
  const level = isPerGroup ? null : (customRanges ? getLevelFromRanges(scores.grandTotal, customRanges) : getDefaultLevel(pct));

  /* Per-group: calculate group totals */
  const groupResults = isPerGroup ? (interp.groups || []).map(group => {
    const groupScore = scores.blocks
      .filter(b => group.block_ids.includes(b.blockId))
      .reduce((s, b) => s + b.score, 0);
    const groupMax = scores.blocks
      .filter(b => group.block_ids.includes(b.blockId))
      .reduce((s, b) => s + b.maxPossible, 0);
    const groupLevel = getLevelFromRanges(groupScore, group.rules);
    return { ...group, score: groupScore, maxPossible: groupMax, level: groupLevel };
  }) : [];

  return (
    <div className="max-w-2xl mx-auto pb-24 animate-fade-in" data-testid="scoring-results">
      {/* Header */}
      <div className="text-center pt-6 pb-4">
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${template.color}15` }}>
          <Trophy size={32} style={{ color: template.color }} />
        </div>
        <h1 className="text-2xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Resultados de tu Evaluaci&oacute;n
        </h1>
        <p className="text-xs text-gray-400 mt-1">{template.name}</p>
      </div>

      {/* === PER-GROUP MODE === */}
      {isPerGroup ? (
        <div className="space-y-5 mb-6">
          {groupResults.map(group => {
            const gl = group.level;
            return (
              <div key={group.name} className="rounded-2xl border overflow-hidden shadow-sm" style={{ borderColor: `${gl.color}30` }} data-testid={`group-result-${group.name}`}>
                {/* Group header */}
                <div className="px-5 pt-5 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{group.name}</h3>
                      {group.subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{group.subtitle}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black" style={{ color: gl.color, fontFamily: 'Barlow Condensed, sans-serif' }}>{group.score}</p>
                      <p className="text-[10px] text-gray-400">de {group.maxPossible} pts</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: gl.color }}>
                      {gl.label}
                    </span>
                  </div>
                </div>
                {/* Group interpretation */}
                <div className="px-5 pb-5">
                  {gl.description && <p className="text-sm text-gray-600 leading-relaxed">{gl.description}</p>}
                  {gl.advice && (
                    <div className="mt-3 rounded-xl p-3" style={{ backgroundColor: `${gl.color}08` }}>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Recomendaci&oacute;n</p>
                      <p className="text-sm font-semibold text-gray-800 leading-relaxed">{gl.advice}</p>
                    </div>
                  )}
                  {gl.warning && (
                    <div className="mt-2 flex items-start gap-2 bg-amber-50 rounded-xl p-3">
                      <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs font-semibold text-amber-700 leading-relaxed">{gl.warning}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* === GLOBAL SCORE MODE === */
        <>
          {/* Grand Total Card */}
          <div className={`rounded-2xl border-2 p-6 mb-6 text-center ${level.bg} ${level.border}`} data-testid="grand-total-card">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Gran Total</p>
            <p className="text-5xl font-black" style={{ color: level.color, fontFamily: 'Barlow Condensed, sans-serif' }}>
              {scores.grandTotal}
            </p>
            <p className="text-sm text-gray-500 mt-1">de {scores.maxTotal} puntos posibles</p>
            <div className="mt-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: level.color }}>
                {level.label}
              </span>
            </div>
          </div>

          {/* Interpretation: Description + Advice */}
          {(level.description || level.advice) && (
            <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: `${level.color}30`, backgroundColor: `${level.color}06` }} data-testid="scoring-interpretation">
              {level.description && (
                <p className="text-sm text-gray-700 leading-relaxed">{level.description}</p>
              )}
              {level.advice && (
                <div className="mt-3 flex gap-2.5">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${level.color}20` }}>
                    <ChevronRight size={12} style={{ color: level.color }} />
                  </div>
                  <p className="text-sm font-semibold text-gray-800 leading-relaxed">{level.advice}</p>
                </div>
              )}
            </div>
          )}

          {/* Section Breakdown */}
          <div className="space-y-3 mb-8">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Desglose por Secci&oacute;n</h3>
            {scores.blocks.filter(block => block.maxPossible > 0).map(block => {
              const blockPct = block.maxPossible > 0 ? (block.score / block.maxPossible) * 100 : 0;
              return (
                <div key={block.name} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" data-testid={`score-block-${block.name}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-[#1A2E10]">{block.name}</p>
                    <p className="text-lg font-black" style={{ color: block.color }}>
                      {block.score} <span className="text-xs text-gray-400 font-semibold">/ {block.maxPossible}</span>
                    </p>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${blockPct}%`, backgroundColor: block.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
        <p className="text-xs text-amber-700 leading-relaxed">
          <span className="font-bold">Nota:</span> Estos resultados son orientativos y no constituyen un diagn&oacute;stico m&eacute;dico.
          Comparte este resultado con tu distribuidor o profesional de salud para una interpretaci&oacute;n personalizada.
        </p>
      </div>

      {/* Final messages */}
      {isPerGroup && interp.final_message && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="scoring-final-message">
          <p className="text-sm text-blue-800 leading-relaxed font-medium">{interp.final_message}</p>
        </div>
      )}
      {!isPerGroup && template.scoring_message && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="scoring-final-message">
          <p className="text-sm text-blue-800 leading-relaxed font-medium">{template.scoring_message}</p>
        </div>
      )}

      {/* Button */}
      <Button
        onClick={onClose}
        className="w-full bg-[#467322] hover:bg-[#3A601C] text-white py-3"
        data-testid="scoring-done-btn"
      >
        <Check size={16} className="mr-2" /> Finalizar
      </Button>
    </div>
  );
};


/* ── Main Survey Component ────────────────────────────────── */
const DynamicHealthSurvey = ({ backPath, backLabel }) => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentBlock, setCurrentBlock] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [unanswered, setUnanswered] = useState([]);
  const [scoringResult, setScoringResult] = useState(null);
  const questionRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/health-templates/${templateId}`)
      .then(res => setTemplate(res.data))
      .catch(() => { toast.error('Test no encontrado'); navigate(backPath || '/client/health'); })
      .finally(() => setLoading(false));
  }, [templateId, navigate, backPath]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setUnanswered([]);
  }, [currentBlock]);

  const setAnswer = useCallback((qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
    setUnanswered(prev => prev.filter(id => id !== qId));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;
  if (!template) return null;

  /* Show scoring results if available */
  if (scoringResult) {
    return (
      <ScoringResults
        template={template}
        scores={scoringResult}
        onClose={() => navigate(backPath || '/client/health')}
      />
    );
  }

  const blocks = template.blocks || [];
  const block = blocks[currentBlock];
  const totalQuestions = blocks.reduce((sum, b) => sum + (b.questions?.length || 0), 0);
  const answeredCount = Object.keys(answers).length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  const isLast = currentBlock === blocks.length - 1;

  const validateBlock = () => {
    const questions = block?.questions || [];
    const missing = questions.filter(q => {
      const a = answers[q.id];
      if (!a) return true;
      if (typeof a === 'string' && a.trim() === '') return true;
      return false;
    });

    if (missing.length > 0) {
      setUnanswered(missing.map(q => q.id));
      const firstMissing = missing[0];
      const el = questionRefs.current[firstMissing.id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const nums = missing.map(q => {
        const idx = questions.findIndex(qq => qq.id === q.id);
        return idx + 1;
      });
      toast.error(`Responde ${missing.length === 1 ? 'la pregunta' : 'las preguntas'} ${nums.join(', ')} antes de continuar`);
      return false;
    }
    setUnanswered([]);
    return true;
  };

  const handleNext = () => {
    if (validateBlock()) {
      setCurrentBlock(Math.min(blocks.length - 1, currentBlock + 1));
    }
  };

  const handleSubmit = async () => {
    if (!validateBlock()) return;
    setSaving(true);
    try {
      const payload = { template_id: template.id, answers };

      /* Calculate scores for scored templates */
      if (template.has_scoring) {
        const scores = calculateScores(template, answers);
        payload.scores = scores;
      }

      await axios.post(`${API}/health-surveys/submit`, payload);

      if (template.has_scoring) {
        const scores = calculateScores(template, answers);
        toast.success('Evaluacion completada');
        setScoringResult(scores);
      } else {
        toast.success('Evaluacion completada exitosamente');
        navigate(backPath || '/client/health');
      }
    } catch {
      toast.error('Error al enviar la evaluacion');
    } finally {
      setSaving(false);
    }
  };

  const IconComp = ICON_MAP[block?.icon] || FileText;

  return (
    <div className="max-w-2xl mx-auto pb-24 animate-fade-in" data-testid="dynamic-health-survey">
      {/* Micro sticky: only progress line */}
      <div className="sticky top-14 bg-zinc-50 z-30 pt-1 pb-1.5">
        <Progress value={progress} className="h-1" />
      </div>

      {/* Navigation row */}
      <div className="flex items-center justify-between py-3">
        <button onClick={() => currentBlock > 0 ? setCurrentBlock(currentBlock - 1) : navigate(backPath || '/client/health')}
          className="flex items-center gap-1.5 text-[#467322] font-semibold text-xs"
          data-testid="survey-back-btn">
          <ArrowLeft size={14} /> {currentBlock > 0 ? 'Anterior' : (backLabel || 'Volver')}
        </button>
        <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
          Bloque {currentBlock + 1} de {blocks.length} — {answeredCount}/{totalQuestions}
        </span>
      </div>

      {/* Block Header */}
      {block && (
        <div className="mb-5">
          <div className="rounded-2xl p-5 border" style={{ backgroundColor: `${block.color}08`, borderColor: `${block.color}20` }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${block.color}18` }}>
                <IconComp size={22} style={{ color: block.color }} />
              </div>
              <div>
                <h2 className="text-xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {block.name}
                </h2>
                <p className="text-[10px] font-bold text-gray-400 mt-0.5">{block.questions?.length} preguntas</p>
              </div>
            </div>
            {block.description && (
              <p className="text-xs text-gray-500 leading-relaxed">{block.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-5">
        {(block?.questions || []).map((q, qi) => {
          const isUnansweredQ = unanswered.includes(q.id);
          return (
            <div
              key={q.id}
              ref={el => { questionRefs.current[q.id] = el; }}
              className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all duration-300 ${
                isUnansweredQ ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-100'
              }`}
              data-testid={`question-${q.id}`}
            >
              {/* Question header */}
              <div className="p-5 pb-3">
                <p className="font-black text-lg text-[#1A2E10] leading-snug" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <span className="text-gray-300 mr-1.5">{qi + 1}.</span> {q.text}
                </p>
                {q.explanation && (
                  <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">{q.explanation}</p>
                )}
                {isUnansweredQ && (
                  <div className="flex items-center gap-1.5 mt-2 text-red-500" data-testid={`unanswered-${q.id}`}>
                    <AlertCircle size={13} />
                    <span className="text-[11px] font-semibold">Esta pregunta es obligatoria</span>
                  </div>
                )}
              </div>

              {/* Answers */}
              <div className="px-5 pb-5">
                {/* Open-ended */}
                {q.type === 'open' && (
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={e => setAnswer(q.id, e.target.value)}
                    placeholder="Escribe tu respuesta..."
                    className={`min-h-[80px] text-sm focus:border-[#467322] focus:ring-[#467322]/20 ${
                      isUnansweredQ ? 'border-red-300' : 'border-gray-200'
                    }`}
                    data-testid={`answer-${q.id}`}
                  />
                )}

                {/* Time picker */}
                {q.type === 'time' && (
                  <TimePicker
                    value={answers[q.id] || ''}
                    onChange={val => setAnswer(q.id, val)}
                    isUnanswered={isUnansweredQ}
                    testId={`answer-${q.id}`}
                  />
                )}

                {/* Number input */}
                {q.type === 'number' && (
                  <NumberInput
                    value={answers[q.id] || ''}
                    onChange={val => setAnswer(q.id, val)}
                    unit={q.unit || ''}
                    min={q.min ?? 0}
                    max={q.max ?? 999}
                    isUnanswered={isUnansweredQ}
                    testId={`answer-${q.id}`}
                  />
                )}

                {/* Scale 1-4 */}
                {q.type === 'scale' && (
                  <div className="grid grid-cols-2 gap-2" data-testid={`answer-${q.id}`}>
                    {(q.options || []).map(opt => {
                      const val = opt.value || opt.label;
                      const selected = answers[q.id] === val;
                      const colorMap = { '1': '#059669', '2': '#F59E0B', '3': '#F97316', '4': '#EF4444' };
                      const accentColor = colorMap[val] || '#6B7280';
                      return (
                        <button
                          key={val}
                          onClick={() => setAnswer(q.id, val)}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 ${
                            selected
                              ? 'shadow-sm'
                              : 'border-gray-100 bg-gray-50/50 hover:border-gray-200'
                          }`}
                          style={selected ? { borderColor: accentColor, backgroundColor: `${accentColor}08` } : {}}
                          data-testid={`answer-${q.id}-scale-${val}`}
                        >
                          <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0 ${
                            selected ? 'text-white' : 'text-gray-400 bg-gray-100'
                          }`} style={selected ? { backgroundColor: accentColor } : {}}>
                            {val}
                          </span>
                          <span className={`text-xs font-semibold leading-tight ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
                            {opt.label}
                          </span>
                          {selected && <Check size={14} className="ml-auto flex-shrink-0" style={{ color: accentColor }} />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Scored Select — severity strip */}
                {q.type === 'scored_select' && (() => {
                  const optLen = (q.options || []).length;
                  const gridClass = optLen <= 2 ? 'grid-cols-2' : optLen <= 4 ? 'grid-cols-4' : 'grid-cols-3 sm:grid-cols-5';
                  return (
                  <div className={`grid gap-2 ${gridClass}`} data-testid={`answer-${q.id}`}>
                    {(q.options || []).map(opt => {
                      const selected = answers[q.id] === opt.value;
                      const accentColor = SEVERITY_COLORS[opt.value] || '#6B7280';
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setAnswer(q.id, opt.value)}
                          className={`relative flex flex-col items-center justify-center px-1.5 py-2.5 rounded-xl border text-center transition-all duration-200 ${
                            selected
                              ? 'shadow-md ring-1'
                              : 'border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-gray-50'
                          }`}
                          style={selected ? { borderColor: accentColor, backgroundColor: `${accentColor}10`, ringColor: `${accentColor}40` } : {}}
                          data-testid={`answer-${q.id}-scored-${opt.value}`}
                        >
                          {selected && (
                            <div className="absolute top-1 right-1">
                              <Check size={12} style={{ color: accentColor }} />
                            </div>
                          )}
                          <span className={`text-[11px] font-bold leading-tight ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
                            {opt.label}
                          </span>
                          {opt.points > 0 && (
                            <span className={`text-[9px] mt-0.5 ${selected ? 'font-bold' : 'font-medium text-gray-300'}`} style={selected ? { color: accentColor } : {}}>
                              {opt.points} pts
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  );
                })()}

                {/* Select pills */}
                {q.type === 'select' && (
                  <div className="flex flex-wrap gap-2">
                    {(q.options || []).map(opt => {
                      const optValue = typeof opt === 'string' ? opt : opt.label;
                      const selected = answers[q.id] === optValue;
                      return (
                        <button
                          key={optValue}
                          onClick={() => setAnswer(q.id, optValue)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                            selected
                              ? 'border-[#467322] bg-[#467322] text-white shadow-sm'
                              : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300 hover:bg-gray-100'
                          }`}
                          data-testid={`answer-${q.id}-${optValue}`}
                        >
                          {selected && <Check size={11} className="inline mr-1 -mt-0.5" />}
                          {optValue}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Bristol Scale with images */}
                {q.type === 'bristol' && (
                  <div className="space-y-2">
                    {(q.options || []).map(opt => {
                      const optId = opt.id || opt.label;
                      const selected = answers[q.id] === String(optId);
                      const bristolInfo = BRISTOL_DATA_LIST.find(b => b.id === opt.id);
                      const bristolColor = bristolInfo?.color || '#6B7280';
                      const bristolImage = bristolInfo?.image;
                      return (
                        <button
                          key={optId}
                          onClick={() => setAnswer(q.id, String(optId))}
                          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition-all duration-200 ${
                            selected
                              ? 'border-[#467322] bg-[#467322]/5 shadow-sm'
                              : 'border-gray-100 bg-gray-50/50 hover:border-gray-200'
                          }`}
                          data-testid={`answer-${q.id}-bristol-${optId}`}
                        >
                          {/* Bristol image */}
                          <div className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 bg-white border border-gray-100 overflow-hidden">
                            {bristolImage ? (
                              <img src={bristolImage} alt={`Tipo ${opt.id}`} className="w-12 h-12 object-contain" loading="lazy" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-black text-sm text-white"
                                style={{ backgroundColor: bristolColor }}>
                                {opt.id}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-bold text-xs text-gray-700">{opt.label || `Tipo ${opt.id}`}</p>
                            <p className="text-[10px] text-gray-400 leading-snug">{opt.description}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] text-white"
                              style={{ backgroundColor: bristolColor }}>
                              {opt.id}
                            </div>
                            {selected && <Check size={16} className="text-[#467322]" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 gap-4">
        <Button
          variant="outline"
          onClick={() => setCurrentBlock(Math.max(0, currentBlock - 1))}
          disabled={currentBlock === 0}
          className="flex-1"
          data-testid="survey-prev-btn"
        >
          <ChevronLeft size={16} className="mr-1" /> Anterior
        </Button>
        {isLast ? (
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 bg-[#467322] hover:bg-[#3A601C] text-white"
            data-testid="survey-submit-btn"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-1" />}
            Enviar Evaluacion
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            className="flex-1 bg-[#467322] hover:bg-[#3A601C] text-white"
            data-testid="survey-next-btn"
          >
            Siguiente <ChevronRight size={16} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default DynamicHealthSurvey;
